// src/features/groups/GroupManager.ts
/**
 * Group Manager - Manages element groups for Flowi
 * Works with existing React + absolute positioning architecture
 */

import { ElementGroup, GroupableElement, GroupOperationResult } from './groupTypes';

export class GroupManager {
    private groups: Map<string, ElementGroup> = new Map();
    private elementToGroup: Map<string, string> = new Map(); // elementId -> groupId
    private listeners: Set<() => void> = new Set();
    private nextGroupId = 1;

    /**
     * Create a new group from elements
     */
    createGroup(
        elements: GroupableElement[],
        name?: string
    ): GroupOperationResult {
        if (elements.length < 2) {
            return {
                success: false,
                error: 'At least 2 elements required to create a group'
            };
        }

        // Check if elements already grouped
        const alreadyGrouped = elements.filter(el =>
            this.elementToGroup.has(this.getElementKey(el))
        );

        if (alreadyGrouped.length > 0) {
            return {
                success: false,
                error: 'Some elements are already in groups'
            };
        }

        const groupId = `group_${this.nextGroupId++}`;
        const group: ElementGroup = {
            id: groupId,
            name: name || `Group ${this.nextGroupId - 1}`,
            elements: [...elements],
            locked: false,
            layerOrder: this.groups.size,
            metadata: {
                createdAt: Date.now(),
                updatedAt: Date.now()
            }
        };

        this.groups.set(groupId, group);

        // Update lookup
        elements.forEach(el => {
            this.elementToGroup.set(this.getElementKey(el), groupId);
        });

        this.notifyListeners();

        return { success: true, groupId, affectedElements: elements };
    }

    /**
     * Remove a group (elements stay, just ungrouped)
     */
    ungroup(groupId: string): GroupOperationResult {
        const group = this.groups.get(groupId);
        if (!group) {
            return { success: false, error: 'Group not found' };
        }

        // Remove from lookup
        group.elements.forEach(el => {
            this.elementToGroup.delete(this.getElementKey(el));
        });

        this.groups.delete(groupId);
        this.notifyListeners();

        return { success: true, affectedElements: group.elements };
    }

    /**
     * Add element to existing group
     */
    addToGroup(groupId: string, element: GroupableElement): GroupOperationResult {
        const group = this.groups.get(groupId);
        if (!group) {
            return { success: false, error: 'Group not found' };
        }

        const key = this.getElementKey(element);
        if (this.elementToGroup.has(key)) {
            return { success: false, error: 'Element already in a group' };
        }

        group.elements.push(element);
        this.elementToGroup.set(key, groupId);
        group.metadata.updatedAt = Date.now();

        this.notifyListeners();
        return { success: true, groupId, affectedElements: [element] };
    }

    /**
     * Remove element from its group
     */
    removeFromGroup(element: GroupableElement): GroupOperationResult {
        const key = this.getElementKey(element);
        const groupId = this.elementToGroup.get(key);

        if (!groupId) {
            return { success: false, error: 'Element not in any group' };
        }

        const group = this.groups.get(groupId);
        if (!group) {
            return { success: false, error: 'Group not found' };
        }

        group.elements = group.elements.filter(
            el => this.getElementKey(el) !== key
        );
        this.elementToGroup.delete(key);
        group.metadata.updatedAt = Date.now();

        // Remove group if empty
        if (group.elements.length === 0) {
            this.groups.delete(groupId);
        }

        this.notifyListeners();
        return { success: true, affectedElements: [element] };
    }

    /**
     * Get group by ID
     */
    getGroup(groupId: string): ElementGroup | undefined {
        return this.groups.get(groupId);
    }

    /**
     * Get group containing an element
     */
    getGroupForElement(element: GroupableElement): ElementGroup | undefined {
        const groupId = this.elementToGroup.get(this.getElementKey(element));
        return groupId ? this.groups.get(groupId) : undefined;
    }

    /**
     * Get all groups
     */
    getAllGroups(): ElementGroup[] {
        return Array.from(this.groups.values());
    }

    /**
     * Check if element is in a group
     */
    isGrouped(element: GroupableElement): boolean {
        return this.elementToGroup.has(this.getElementKey(element));
    }

    /**
     * Get all elements from a group (resolves nested groups)
     */
    getGroupElements(groupId: string, recursive = false): GroupableElement[] {
        const group = this.groups.get(groupId);
        if (!group) return [];

        if (!recursive) {
            return [...group.elements];
        }

        // Recursively get elements from child groups
        const elements: GroupableElement[] = [];
        const visited = new Set<string>();

        const collect = (gId: string) => {
            if (visited.has(gId)) return;
            visited.add(gId);

            const g = this.groups.get(gId);
            if (!g) return;

            g.elements.forEach(el => {
                const childGroupId = this.elementToGroup.get(this.getElementKey(el));
                if (childGroupId && childGroupId !== gId) {
                    collect(childGroupId);
                } else {
                    elements.push(el);
                }
            });
        };

        collect(groupId);
        return elements;
    }

    /**
     * Set group locked state
     */
    setGroupLocked(groupId: string, locked: boolean): void {
        const group = this.groups.get(groupId);
        if (group) {
            // Create a new group object to trigger React re-renders
            const updatedGroup = {
                ...group,
                locked,
                metadata: {
                    ...group.metadata,
                    updatedAt: Date.now()
                }
            };
            this.groups.set(groupId, updatedGroup);
            this.notifyListeners();
        }
    }

    /**
     * Rename group
     */
    renameGroup(groupId: string, name: string): GroupOperationResult {
        const group = this.groups.get(groupId);
        if (!group) {
            return { success: false, error: 'Group not found' };
        }

        // Create a new group object to trigger React re-renders
        const updatedGroup = {
            ...group,
            name,
            metadata: {
                ...group.metadata,
                updatedAt: Date.now()
            }
        };
        this.groups.set(groupId, updatedGroup);
        this.notifyListeners();

        return { success: true, groupId };
    }

    /**
     * Set master group ID (link to Supabase master group)
     */
    setMasterGroupId(groupId: string, masterGroupId: string): GroupOperationResult {
        const group = this.groups.get(groupId);
        if (!group) {
            return { success: false, error: 'Group not found' };
        }

        // Create a new group object to trigger React re-renders
        const updatedGroup = {
            ...group,
            masterGroupId,
            metadata: {
                ...group.metadata,
                updatedAt: Date.now()
            }
        };
        this.groups.set(groupId, updatedGroup);
        this.notifyListeners();

        return { success: true, groupId };
    }

    /**
     * Load groups from saved data
     */
    loadGroups(savedGroups: ElementGroup[]): void {
        // Only clear and load if we actually have saved groups
        // This prevents clearing existing groups when loading diagrams with no saved groups
        if (!savedGroups || savedGroups.length === 0) {
            return;
        }

        this.clear();

        // Restore groups and rebuild lookup maps
        savedGroups.forEach(group => {
            this.groups.set(group.id, group);

            // Rebuild element to group mapping
            group.elements.forEach(el => {
                this.elementToGroup.set(this.getElementKey(el), group.id);
            });

            // Update nextGroupId to avoid conflicts
            const numericId = parseInt(group.id.replace('group_', ''));
            if (!isNaN(numericId) && numericId >= this.nextGroupId) {
                this.nextGroupId = numericId + 1;
            }
        });

        this.notifyListeners();
    }

    /**
     * Subscribe to changes
     */
    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /**
     * Clear all groups
     */
    clear(): void {
        this.groups.clear();
        this.elementToGroup.clear();
        this.notifyListeners();
    }

    // Private helpers

    private getElementKey(element: GroupableElement): string {
        return `${element.type}:${element.id}`;
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener());
    }
}

// Singleton instance
export const groupManager = new GroupManager();